import { useMemo } from 'react';

import { Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import BoxIcon from '@/components/Icons/BoxIcon';
import SearchIcon from '@/components/Icons/SearchIcon';
import Loading from '@/components/Loading';
import { BasetenPageEnum, usePageSwitcher } from '@/routes';
import { useOrgBudget } from '@/sections/Billing/hooks';
import ViewModes from '@/sections/Workspace/Filter/ViewModes';
import { ViewMode } from '@/sections/Workspace/types';
import Fuse from 'fuse.js';

import { BASE_FUSE_SEARCH_OPTIONS } from '../constants';
import { useFilterQuery, useViewMode } from '../hooks';
import EmptyState from '../sections/EmptyStates';
import { IconContainer } from '../styled';
import { WorkspaceSections } from '../types';
import ModelAPIsEmptyState from './EmptyState';
import Grid from './Grid';
import Table from './Table';
import { WorkspaceOrgSharedEndpointsFragment } from './__generated__/fragments.generated';
import { useOrgSharedEndpoints } from './hooks';

const FUSE_SEARCH_OPTIONS = {
  ...BASE_FUSE_SEARCH_OPTIONS,
  keys: ['id', 'sharedEndpoint.id', 'sharedEndpoint.name'],
};

function ModelAPIs() {
  const { orgSharedEndpoints, loading } = useOrgSharedEndpoints(true);
  const { viewMode, setViewMode } = useViewMode(WorkspaceSections.ModelAPIs);
  const { filterQuery, setFilterQuery } = useFilterQuery(WorkspaceSections.ModelAPIs);
  const goToPage = usePageSwitcher();
  const { isSpendExceeded } = useOrgBudget();

  const filteredOrgSharedEndpoints = useMemo(() => {
    if (!filterQuery) {
      return orgSharedEndpoints;
    }

    const fuse = new Fuse<WorkspaceOrgSharedEndpointsFragment>(
      orgSharedEndpoints,
      FUSE_SEARCH_OPTIONS,
    );
    return fuse.search(filterQuery).map(({ item }) => item);
  }, [filterQuery, orgSharedEndpoints]);

  function goToCreateModelAPIPage() {
    goToPage(BasetenPageEnum.CreateSharedEndpoint);
  }

  function getContent() {
    if (loading) {
      return <Loading />;
    }

    if (isEmpty(orgSharedEndpoints)) {
      return <ModelAPIsEmptyState />;
    }

    if (isEmpty(filteredOrgSharedEndpoints)) {
      return <EmptyState>No Model APIs found</EmptyState>;
    }

    return viewMode === ViewMode.Grid ? (
      <Grid orgSharedEndpoints={filteredOrgSharedEndpoints} isSpendExceeded={isSpendExceeded} />
    ) : (
      <Table orgSharedEndpoints={filteredOrgSharedEndpoints} isSpendExceeded={isSpendExceeded} />
    );
  }

  return (
    <Stack height="100%">
      <Stack gap={3} px={4} pt={4} pb={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer>
              <BoxIcon />
            </IconContainer>
            <Typography variant="h2">Model APIs</Typography>
          </Stack>
          <Button variant="contained" color="primary" onClick={goToCreateModelAPIPage}>
            Explore Model APIs
          </Button>
        </Stack>
        <Stack direction="row" gap={1}>
          <TextField
            placeholder="Find by name"
            sx={{ minWidth: 400 }}
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <ViewModes viewMode={viewMode} setViewMode={setViewMode} />
        </Stack>
      </Stack>
      <Stack flex={1}>{getContent()}</Stack>
    </Stack>
  );
}

export default ModelAPIs;
